import { mergeAll } from 'ramda';

import { mappedSearch } from '../actions/mapped-search';
import {
  getDocumentByUid,
  getSingleTypeDocument,
  getDocumentById,
} from '../actions/prismic';
import {
  getDocByUid,
  getSingleTypeDoc,
  getDocById,
} from '../selectors/entities';
import {
  getDocByUidError,
  getSingleTypeDocError,
  getDocByIdError,
} from '../selectors/errors';
import {
  getFullResultsByTheirOwnIdentifier,
  isAnyPageError,
} from '../selectors/search';
import { SSRComponent } from '../types/component';

/**
 * Helper function that await the getInitialProps method of the given component
 */
export const withInitFrom =
  <T = any>(comp: SSRComponent<T>, props?: T) =>
  (data: any) =>
    comp.getInitialProps(data, props);

/**
 * Helper function that changes the response status in data
 */
export const withStatus = (status: number) => (data: any) => {
  data.res.status(status);
  return data;
};

/**
 * Helper function that awaits the given redux thunk
 */
export const withThunk =
  (thunk: (dispatch: any, getState: any) => Promise<void>) =>
  async (data: any) => {
    const { store } = data;
    const { dispatch, getState } = store;
    await thunk(dispatch, getState);
    return data;
  };

/**
 * Helper function that load a single type document synchronously
 */
export const syncSingleType = async <T = any>(
  singleTypeId: string,
  serverData: any
): Promise<{ error: any; data: T }> => {
  await withThunk(getSingleTypeDocument(singleTypeId))(serverData);
  const state = serverData.store.getState();
  return {
    data: getSingleTypeDoc(singleTypeId, state),
    error: getSingleTypeDocError(singleTypeId, state),
  };
};

/**
 * Helper function that load a document by uid synchronously
 */
export const syncDocumentByUid = async <T = any>(
  customTypeId: string,
  uid: string,
  serverData: any
): Promise<{ error: any; data: T }> => {
  await withThunk(getDocumentByUid(customTypeId, uid))(serverData);
  const state = serverData.store.getState();
  return {
    data: getDocByUid(customTypeId, uid, state),
    error: getDocByUidError(customTypeId, uid, state),
  };
};

/**
 * Helper function that load a document by id synchronously
 */
export const syncDocumentById = async <T = any>(
  customTypeId: string,
  id: string,
  serverData: any
): Promise<{ error: any; data: T }> => {
  await withThunk(getDocumentById(id))(serverData);
  const state = serverData.store.getState();
  return {
    data: getDocById(customTypeId, id, state),
    error: getDocByIdError(id, state),
  };
};

export const syncConnectedGrid = async <T = any>(
  key: string,
  page: number,
  filters: any,
  serverData: any,
  excludedFromFacetting?: string[],
  keySuffix?: string
): Promise<{ error: any; data: T[] }> => {
  const searchKey = keySuffix ? `${key}-${keySuffix}` : key;
  await withThunk(
    mappedSearch[key](
      searchKey,
      page,
      { query: filters },
      true,
      excludedFromFacetting || []
    )
  )(serverData);
  const state = serverData.store.getState();
  return {
    data: getFullResultsByTheirOwnIdentifier(key, state),
    error: isAnyPageError(key, state),
  };
};

/**
 * Helper function that performs all the given loaders an data, and merge the result
 * into one flat data object.
 */
export default async (data: any, ...loaders: ((data: any) => Promise<any>)[]) =>
  loaders.length > 0
    ? mergeAll(await Promise.all(loaders.map((l) => l(data))))
    : data;
